import { createRouter } from 'vue-router'
import { site } from '../data'
import { localeOfPath } from '../composables/useLocale'
import { applyPageMeta, applyRobots } from '../composables/usePageMeta'
import features from '../config/features.json'
import Home from '../views/Home.vue'
import Products from '../views/Products.vue'
import ProductDetail from '../views/ProductDetail.vue'
import CaseStudies from '../views/CaseStudies.vue'
import CaseStudyDetail from '../views/CaseStudyDetail.vue'
import About from '../views/About.vue'
import Blog from '../views/Blog.vue'
import BlogPost from '../views/BlogPost.vue'
import Contact from '../views/Contact.vue'
import NotFound from '../views/NotFound.vue'

// Every page exists in English at "/x" and in Arabic at "/ar/x".
const L = '/:locale(ar)?'

// Route name -> key under site.<locale>.seo. Detail pages set their own meta.
const SEO_KEYS = {
  Home: 'home',
  Products: 'solutions',
  Contact: 'contact',
  About: 'about',
  CaseStudies: 'case_studies',
  Blog: 'blog',
  NotFound: 'not_found'
}

const routes = [
  { path: `${L}`, name: 'Home', component: Home },
  { path: `${L}/products`, name: 'Products', component: Products },
  { path: `${L}/products/:slug`, name: 'ProductDetail', component: ProductDetail },
  { path: `${L}/case-studies`, name: 'CaseStudies', component: CaseStudies },
  { path: `${L}/case-studies/:slug`, name: 'CaseStudyDetail', component: CaseStudyDetail },
  { path: `${L}/about`, name: 'About', component: About },
  { path: `${L}/blog`, name: 'Blog', component: Blog },
  { path: `${L}/blog/:slug`, name: 'BlogPost', component: BlogPost },
  { path: `${L}/contact`, name: 'Contact', component: Contact },
  { path: '/:pathMatch(.*)*', name: 'NotFound', component: NotFound }
]

/**
 * Builds a router for the given history: `createWebHistory()` in the browser,
 * `createMemoryHistory()` when prerendering at build time. A fresh router per
 * app keeps the server render free of shared state.
 */
export function createAppRouter (history) {
  const router = createRouter({
    history,
    routes,
    scrollBehavior (to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition
      }
      if (to.hash) {
        return { el: to.hash, behavior: 'smooth' }
      }
      // Switching language keeps the reader where they were.
      if (to.path !== from.path && to.name === from.name && to.params.slug === from.params.slug) {
        return false
      }
      return { top: 0, behavior: 'smooth' }
    }
  })

  // Only runs in the browser. When prerendering, the build script writes the
  // <html lang dir> and head tags for each route itself.
  router.afterEach((to) => {
    if (typeof document === 'undefined') {
      return
    }

    const locale = localeOfPath(to.path)

    document.documentElement.lang = locale
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr'

    // Sections switched off in src/config/features.json stay noindex.
    applyRobots(
      (['CaseStudies', 'CaseStudyDetail'].includes(to.name) && !features.showCaseStudies) ||
      (['Blog', 'BlogPost'].includes(to.name) && !features.showBlog)
    )

    // Detail pages (solution, case study, article) set their own title.
    const key = SEO_KEYS[to.name]
    const seo = key && (site[locale]?.seo?.[key] || site.en?.seo?.[key])
    if (seo) {
      applyPageMeta(seo)
    }
  })

  return router
}
